import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import {
  ShieldCheck, FileText, User, CheckCircle, XCircle, AlertCircle, Loader2,
  Search, RefreshCw, PauseCircle, ArrowLeft, ArrowRight, Upload, Download,
  FileCheck2, Send, Plus, Inbox, Link2, AlertTriangle, Check, X, Briefcase,
} from 'lucide-react';
import { toast } from 'sonner';
import QuickBgvPanel from '../components/quickbgv/QuickBgvPanel';

// ===========================================================================
// DCS ATS — Background Verification
// ===========================================================================
//
// FIVE RULES THIS FILE MUST NOT BREAK
// -----------------------------------
//
// 1. EVERY REQUEST GOES THROUGH `api` (../lib/api).
//    The previous version used bare fetch() with a hand-built Authorization
//    header and never checked res.ok. fetch() resolves for 401/403/422 as
//    happily as for 200, so an expired token, a role refusal or a rejected
//    query parameter all arrived as `{detail: "..."}`, failed an
//    Array.isArray() test, and rendered as an empty list with no message. That
//    is precisely what "searching isn't showing up in the dropdown" looked
//    like. axios rejects on any non-2xx, so those cases now reach a catch
//    block, and lib/api's interceptor handles a genuinely dead session.
//
// 2. AN EMPTY LIST AND A FAILED REQUEST MUST NEVER LOOK THE SAME.
//    Every panel below carries an explicit `error` state and renders it. If a
//    list is empty, it is empty because the server said so.
//
// 3. SEARCHING IS THE SERVER'S JOB.
//    The pool is >2,000 records. The old dialog downloaded one page and
//    filtered it in the browser with String.includes(), so anyone outside that
//    page could not be found however carefully you typed their name. The
//    picker below calls /bgv/candidate-search, debounced, with a stale-response
//    guard.
//
// 4. THE PAGE NEVER DEMANDS A UUID.
//    Recruiters do not know candidate ids and cannot look one up. Everything
//    here is chosen from a list.
//
// 5. NO DEAD ENDS. Wherever an action is refused, the same screen offers the
//    fix — most importantly the "no role linked" banner, which lists the roles
//    this candidate is already on.

// ---- constants mirrored from backend/routes/bgv.py (the contract) ----
const STAGE_KINDS = ['salary_ctc', 'employment', 'education', 'criminal_address'];
const STAGE_LABEL = {
  salary_ctc: 'Salary / CTC', employment: 'Employment',
  education: 'Education', criminal_address: 'Identity & Address',
};
const STAGE_STATUSES = ['pending', 'in_progress', 'clear', 'discrepancy', 'unable_to_verify'];
const STAGE_STATUS_LABEL = {
  pending: 'Pending', in_progress: 'In progress', clear: 'Clear',
  discrepancy: 'Discrepancy', unable_to_verify: 'Unable to verify',
};
const CASE_STATUSES = ['awaiting_docs', 'in_progress', 'completed', 'on_hold', 'cancelled'];
const CASE_STATUS_LABEL = {
  awaiting_docs: 'Awaiting Docs', in_progress: 'In Progress',
  completed: 'Completed', on_hold: 'On Hold', cancelled: 'Cancelled',
};
const VERDICTS = ['clear', 'minor_discrepancy', 'major_discrepancy', 'unable_to_verify'];
const VERDICT_LABEL = {
  clear: 'Clear', minor_discrepancy: 'Minor discrepancy',
  major_discrepancy: 'Major discrepancy', unable_to_verify: 'Unable to verify',
};

// House style (matches the Mailbox and the rest of the new DCS screens).
const C = {
  cream: '#faf7f0', card: '#ffffff', border: '#e5e1d5',
  ink: '#2b2b2b', muted: '#7a7a7a', gold: '#c9a84c', navy: '#2e3a68',
};
const FONT = "'Outfit', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";

const SEARCH_DEBOUNCE_MS = 250;

// Roles the backend lets change a case. Kept in step with BGV_ADMIN_ROLES in
// routes/bgv_intake.py. This only decides which BUTTONS are drawn — the server
// refuses the request either way, so a stale copy here is a cosmetic bug, not a
// security one.
const ADMIN_ROLES = ['admin', 'super_admin', 'office_admin'];

/** The signed-in user's role, or '' if the stored profile is unreadable.
 *  Wrapped because localStorage can throw in a private window, and a page that
 *  cannot render because of a storage quirk is worse than one that assumes the
 *  lower privilege. */
function currentRole() {
  try {
    return (JSON.parse(localStorage.getItem('ats_user') || '{}').role) || '';
  } catch (e) {
    return '';
  }
}

/** Pull a human sentence out of an axios error. Without this the user sees
 *  "Request failed with status code 409" instead of the message the backend
 *  wrote for exactly this situation. */
const errText = (e) =>
  e?.response?.data?.detail || e?.message || 'Something went wrong';

const caseStatusColor = (s) => ({
  completed: 'bg-green-100 text-green-700',
  in_progress: 'bg-blue-100 text-blue-700',
  awaiting_docs: 'bg-slate-100 text-slate-700',
  on_hold: 'bg-amber-100 text-amber-700',
  cancelled: 'bg-red-100 text-red-700',
}[s] || 'bg-slate-100 text-slate-700');

const stageStatusColor = (s) => ({
  clear: 'bg-green-100 text-green-700 border-green-300',
  discrepancy: 'bg-red-100 text-red-700 border-red-300',
  unable_to_verify: 'bg-amber-100 text-amber-700 border-amber-300',
  in_progress: 'bg-blue-100 text-blue-700 border-blue-300',
  pending: 'bg-slate-100 text-slate-600 border-slate-300',
}[s] || 'bg-slate-100 text-slate-600 border-slate-300');

const caseStatusIcon = (s) => {
  switch (s) {
    case 'completed': return <CheckCircle className="w-4 h-4 text-green-600" />;
    case 'in_progress': return <Loader2 className="w-4 h-4 text-blue-600" />;
    case 'on_hold': return <PauseCircle className="w-4 h-4 text-amber-600" />;
    case 'cancelled': return <XCircle className="w-4 h-4 text-red-600" />;
    default: return <AlertCircle className="w-4 h-4 text-slate-500" />;
  }
};

const stageBadge = (raw) => {
  const s = String(raw || '').toLowerCase().replace(/[^a-z0-9]+/g, '_');
  if (s === 'joined' || s === 'hired') return { label: 'Joined', cls: 'bg-green-100 text-green-700' };
  if (s === 'offer_made' || s === 'offered' || s === 'offer') {
    return { label: 'Offer made', cls: 'bg-amber-100 text-amber-700' };
  }
  if (s === 'selected') return { label: 'Selected', cls: 'bg-blue-100 text-blue-700' };
  return { label: raw || '-', cls: 'bg-slate-100 text-slate-600' };
};

// ---------------------------------------------------------------------------
// Small shared pieces
// ---------------------------------------------------------------------------

/** One consistent way to show a failed request. Never silently swallow. */
function ErrorNote({ message, onRetry }) {
  if (!message) return null;
  return (
    <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
      <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
      <div className="flex-1">
        <p>{message}</p>
        {onRetry && (
          <button onClick={onRetry} className="mt-1 text-xs underline hover:no-underline">
            Try again
          </button>
        )}
      </div>
    </div>
  );
}

function SectionTitle({ icon: Icon, children, count, action }) {
  return (
    <div className="flex items-center justify-between gap-3 mb-3">
      <h3 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide"
          style={{ color: C.muted }}>
        {Icon && <Icon className="w-4 h-4" />}
        {children}
        {count != null && count > 0 && (
          <span className="ml-1 rounded-full px-2 py-0.5 text-[11px] font-semibold"
                style={{ background: C.gold, color: '#fff' }}>{count}</span>
        )}
      </h3>
      {action}
    </div>
  );
}

/** A candidate row, used by the picker, the ready queue and the request list —
 *  one component so all three can never disagree about how a person is shown.
 *  `name_suspect` marks the records where the CV parser wrote a section heading
 *  ("PROFESSIONAL SUMMARY") into the name field; showing that as somebody's
 *  name makes a list unreadable, so the backend substitutes their code and we
 *  flag it here. */
function CandidateLine({ person, right }) {
  return (
    <div className="flex items-center gap-3 min-w-0">
      <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full"
           style={{ background: C.cream, border: `1px solid ${C.border}` }}>
        <User className="w-4 h-4" style={{ color: C.muted }} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium" style={{ color: C.ink }}>
          {person.name}
          {person.name_suspect && (
            <span className="ml-2 text-[11px] font-normal" style={{ color: C.muted }}
                  title="This record's name field holds a CV section heading, not a name. Open the candidate to correct it.">
              name needs fixing
            </span>
          )}
        </p>
        {person.subtitle && (
          <p className="truncate text-xs" style={{ color: C.muted }}>{person.subtitle}</p>
        )}
      </div>
      {right}
    </div>
  );
}

// ===========================================================================
// The page
// ===========================================================================

export default function BgvPage() {
  const [cases, setCases] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  // Whether the backend is allowed to actually transmit anything. Printed on
  // the page so "the client never got the email" and "the send switch is off"
  // can never again look like the same thing.
  const [gate, setGate] = useState(null);

  const role = useMemo(currentRole, []);
  const isAdmin = ADMIN_ROLES.includes(role);

  const loadCases = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get('/bgv/cases');
      setCases(data.cases || []);
      setCounts(data.counts || {});
    } catch (e) {
      // Show what went wrong. An empty case list and a broken endpoint used to
      // look identical here, and the difference matters: one is "no work to do",
      // the other is "the module is not mounted".
      setError(`Could not load BGV cases — ${errText(e)}`);
      setCases([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadCases(); }, [loadCases, reloadKey]);

  // The send gate is advisory: if this call fails the page carries on without
  // the banner rather than blocking on it.
  useEffect(() => {
    let alive = true;
    api.get('/bgv/gate')
      .then(({ data }) => { if (alive) setGate(data); })
      .catch(() => { if (alive) setGate(null); });
    return () => { alive = false; };
  }, [reloadKey]);

  const refreshAll = () => setReloadKey((k) => k + 1);

  const filtered = cases.filter((c) => {
    if (filter !== 'all' && c.status !== filter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (c.candidate_name || '').toLowerCase().includes(q)
        || (c.requirement_title || '').toLowerCase().includes(q)
        || (c.id || '').includes(q);
    }
    return true;
  });

  if (selectedId) {
    return (
      <CaseDetail
        caseId={selectedId}
        isAdmin={isAdmin}
        onBack={() => { setSelectedId(null); refreshAll(); }}
      />
    );
  }

  const tiles = [
    { key: 'all', label: 'All cases', value: cases.length },
    { key: 'awaiting_docs', label: 'Awaiting docs', value: counts.awaiting_docs || 0 },
    { key: 'in_progress', label: 'In progress', value: counts.in_progress || 0 },
    { key: 'on_hold', label: 'On hold', value: counts.on_hold || 0 },
    { key: 'completed', label: 'Completed', value: counts.completed || 0 },
  ];

  return (
    <div className="p-4 sm:p-6" style={{ background: C.cream, fontFamily: FONT, minHeight: '100%' }}
         data-testid="bgv-page">
      {gate && !gate.email_sending_enabled && (
        <div className="mb-4 rounded-lg border px-3 py-2 text-xs flex items-start gap-2"
             style={{ borderColor: '#e6c98a', background: '#fdf6e3', color: '#6b5320' }}
             data-testid="bgv-send-gate-banner">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>
            <b>Sending is off.</b> Every BGV notification is still written and queued —
            e-mails wait in the Email Queue for approval, WhatsApp messages wait in the
            BGV outbox — but nothing is transmitted. Set <code>BGV_SEND_ENABLED=1</code>
            {' '}in the backend environment to turn e-mail on, and
            {' '}<code>BGV_WHATSAPP_ENABLED=1</code> for WhatsApp.
          </span>
        </div>
      )}

      <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-semibold" style={{ color: C.ink }}>
            <ShieldCheck className="w-5 h-5" style={{ color: C.gold }} />
            Background Verification
          </h1>
          <p className="mt-0.5 text-xs" style={{ color: C.muted }}>
            One case per candidate · four checks · signed certificate.
            {isAdmin
              ? ' Anyone the client has said yes to appears below, ready to start.'
              : ' Send a candidate to BGV and an admin will pick it up.'}
          </p>
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <Button variant="outline" onClick={() => setShowCreate(true)}>
              <Plus className="w-4 h-4 mr-2" /> New BGV Case
            </Button>
          )}
          <Button variant="outline" onClick={refreshAll}>
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>
      </div>

      {/* 0. Arrived here from Actions -> Quick BGV on a candidate's profile.
             Renders only when the URL carries ?candidate=<id>; see
             components/quickbgv/QuickBgvPanel.js. `casesReady` is false while
             the list is loading AND when loading it failed, so an empty list
             caused by a broken endpoint is never read as "no case exists". */}
      <QuickBgvPanel
        cases={cases}
        casesReady={!loading && !error}
        isAdmin={isAdmin}
        onOpenCase={setSelectedId}
        onChanged={refreshAll}
      />

      {/* 1. What has been handed to me (admin) / what I have sent (recruiter) */}
      <RequestsPanel isAdmin={isAdmin} reloadKey={reloadKey} onChanged={refreshAll}
                     onOpenCase={setSelectedId} />

      {/* 2. Everyone the client has said yes to who has no case yet */}
      <ReadyQueue isAdmin={isAdmin} reloadKey={reloadKey} onChanged={refreshAll}
                  onOpenCase={setSelectedId} />

      {/* 3. The cases themselves */}
      <SectionTitle icon={ShieldCheck}>Open cases</SectionTitle>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        {tiles.map((t) => (
          <button key={t.key} onClick={() => setFilter(t.key)}
                  className="rounded-xl px-3 py-3 text-center transition-shadow hover:shadow-sm"
                  style={{
                    background: C.card,
                    border: `1px solid ${filter === t.key ? C.gold : C.border}`,
                  }}>
            <p className="text-2xl font-semibold" style={{ color: C.ink }}>{t.value}</p>
            <p className="text-xs" style={{ color: C.muted }}>{t.label}</p>
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4"
                  style={{ color: C.muted }} />
          <Input placeholder="Filter these cases by candidate, role or case id..."
                 value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" />
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {CASE_STATUSES.filter((s) => s !== 'cancelled').map((s) => (
              <SelectItem key={s} value={s}>{CASE_STATUS_LABEL[s]}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="mb-3"><ErrorNote message={error} onRetry={refreshAll} /></div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin" style={{ color: C.muted }} />
        </div>
      ) : filtered.length === 0 ? (
        <Card><CardContent className="p-10 text-center">
          <ShieldCheck className="w-10 h-10 mx-auto mb-3" style={{ color: C.border }} />
          <p className="text-sm" style={{ color: C.muted }}>
            {cases.length === 0
              ? 'No BGV cases yet. Start one from the “Ready for BGV” list above.'
              : 'No case matches this filter.'}
          </p>
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {filtered.map((c) => (
            <button key={c.id} onClick={() => setSelectedId(c.id)}
                    className="w-full rounded-xl px-4 py-3 text-left transition-shadow hover:shadow-sm"
                    style={{ background: C.card, border: `1px solid ${C.border}` }}>
              <div className="flex items-center gap-3">
                <div className="min-w-0 flex-1">
                  <div className="mb-1 flex flex-wrap items-center gap-2">
                    <span className="font-semibold" style={{ color: C.ink }}>
                      {c.candidate_name || 'Candidate'}
                    </span>
                    <Badge className={caseStatusColor(c.status)}>
                      {caseStatusIcon(c.status)}
                      <span className="ml-1">{CASE_STATUS_LABEL[c.status]}</span>
                    </Badge>
                    {c.verdict && <Badge variant="outline">{VERDICT_LABEL[c.verdict]}</Badge>}
                    {c.needs_requirement && (
                      <Badge className="bg-amber-100 text-amber-800">
                        <Link2 className="w-3 h-3 mr-1" /> No role linked
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs" style={{ color: C.muted }}>
                    {c.requirement_title || 'Role not linked yet'}
                    {' · '}Case {String(c.id).slice(0, 8).toUpperCase()}
                    {c.created_at && ` · Opened ${new Date(c.created_at).toLocaleDateString()}`}
                    {c.report_pdf_path && ' · Certificate on file'}
                  </p>
                </div>
                <ArrowRight className="w-4 h-4" style={{ color: C.border }} />
              </div>
            </button>
          ))}
        </div>
      )}

      <NewCaseDialog
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={(id) => { setShowCreate(false); refreshAll(); setSelectedId(id); }}
      />
    </div>
  );
}

// ===========================================================================
// Panel 1 — the recruiter -> admin handoff
// ===========================================================================

function RequestsPanel({ isAdmin, reloadKey, onChanged, onOpenCase }) {
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get('/bgv/requests', { params: { status: 'pending' } });
      setRows(data.requests || []);
    } catch (e) {
      setError(`Could not load the BGV request queue — ${errText(e)}`);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, reloadKey]);

  const accept = async (row) => {
    setBusyId(row.id);
    try {
      const { data } = await api.post(`/bgv/requests/${row.id}/accept`);
      toast.success(`BGV case opened for ${row.candidate_name}.`);
      if (data.requirement_missing) {
        toast.message('This case has no role linked yet — link one before completing it.');
      }
      await load();
      onChanged && onChanged();
      if (data.case_id) onOpenCase(data.case_id);
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusyId(null);
    }
  };

  const decline = async (row) => {
    // A plain prompt on purpose: the reason is the whole value of a decline, and
    // a second dialog is one more thing to get lost behind.
    const reason = window.prompt(`Why is ${row.candidate_name} not ready for BGV?`, '');
    if (reason === null) return;
    setBusyId(row.id);
    try {
      await api.post(`/bgv/requests/${row.id}/decline`, { reason });
      toast.success('Request declined.');
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusyId(null);
    }
  };

  if (!loading && !error && rows.length === 0) return null;

  return (
    <div className="mb-6">
      <SectionTitle icon={Inbox} count={rows.length}>
        {isAdmin ? 'Sent to you' : 'Your BGV requests'}
      </SectionTitle>
      <ErrorNote message={error} onRetry={load} />
      {loading ? (
        <div className="flex items-center gap-2 py-3 text-sm" style={{ color: C.muted }}>
          <Loader2 className="w-4 h-4 animate-spin" /> Loading requests…
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((row) => (
            <div key={row.id} className="rounded-xl px-4 py-3"
                 style={{ background: C.card, border: `1px solid ${C.gold}` }}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <CandidateLine person={{
                  name: row.candidate_name || 'Candidate',
                  subtitle: [
                    row.requirement_title || 'no role linked',
                    `sent by ${row.requested_by_name || 'a recruiter'}`,
                    row.requested_at && new Date(row.requested_at).toLocaleString(),
                  ].filter(Boolean).join(' · '),
                }} />
                {isAdmin ? (
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => accept(row)} disabled={busyId === row.id}>
                      {busyId === row.id
                        ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                        : <Check className="w-3.5 h-3.5 mr-1" />}
                      Start BGV
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => decline(row)}
                            disabled={busyId === row.id}>
                      <X className="w-3.5 h-3.5 mr-1" /> Not yet
                    </Button>
                  </div>
                ) : (
                  <Badge className="bg-amber-100 text-amber-800">Waiting for an admin</Badge>
                )}
              </div>
              {row.note && (
                <p className="mt-2 text-xs italic" style={{ color: C.muted }}>“{row.note}”</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ===========================================================================
// Panel 2 — everyone the client has said yes to
// ===========================================================================

function ReadyQueue({ isAdmin, reloadKey, onChanged, onOpenCase }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/bgv/ready-queue');
      setData(res.data);
    } catch (e) {
      setError(`Could not load the ready-for-BGV list — ${errText(e)}`);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, reloadKey]);

  const start = async (person) => {
    setBusyId(person.id);
    try {
      if (isAdmin) {
        const { data: created } = await api.post('/bgv/cases', {
          candidate_id: person.id,
          requirement_id: person.requirement_id || null,
        });
        toast.success(`BGV case opened for ${person.name}.`);
        await load();
        onChanged && onChanged();
        onOpenCase(created.case.id);
      } else {
        const { data: pushed } = await api.post('/bgv/push', { candidate_id: person.id });
        toast.success(pushed.duplicate
          ? 'Already with an admin — nothing more to do.'
          : `${person.name} sent to BGV. An admin will start it.`);
        await load();
        onChanged && onChanged();
      }
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusyId(null);
    }
  };

  const items = data?.items || [];

  return (
    <div className="mb-6">
      <SectionTitle icon={CheckCircle} count={items.length}
        action={
          <button onClick={load} className="text-xs underline hover:no-underline"
                  style={{ color: C.muted }}>Refresh</button>
        }>
        Ready for BGV
      </SectionTitle>
      <ErrorNote message={error} onRetry={load} />

      {loading ? (
        <div className="flex items-center gap-2 py-3 text-sm" style={{ color: C.muted }}>
          <Loader2 className="w-4 h-4 animate-spin" /> Checking who is ready…
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-xl px-4 py-5 text-sm"
             style={{ background: C.card, border: `1px dashed ${C.border}`, color: C.muted }}>
          Nobody is waiting. This list fills up on its own: the moment a candidate is
          marked <b>Selected</b>, <b>Offer made</b> or <b>Joined</b>, they appear here.
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((person) => {
            const badge = stageBadge(person.status || person.jm_status);
            return (
              <div key={person.id} className="rounded-xl px-4 py-3"
                   style={{ background: C.card, border: `1px solid ${C.border}` }}
                   data-testid="bgv-ready-row">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <CandidateLine person={person}
                    right={<Badge className={badge.cls}>{badge.label}</Badge>} />
                  <div className="flex items-center gap-2">
                    {person.pending_request_id ? (
                      <Badge className="bg-amber-100 text-amber-800">
                        Sent to BGV{person.pending_requested_by
                          ? ` by ${person.pending_requested_by}` : ''}
                      </Badge>
                    ) : (
                      <Button size="sm" onClick={() => start(person)} disabled={busyId === person.id}>
                        {busyId === person.id
                          ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                          : <Send className="w-3.5 h-3.5 mr-1" />}
                        {isAdmin ? 'Start BGV' : 'Send to BGV'}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* People the client said yes to who are not in the candidate pool at all.
          Reported rather than hidden: it is a real and fixable condition (their
          JM pipeline row was never promoted), and hiding it is how people
          quietly fall out of the process. */}
      {data?.not_in_pool_count > 0 && (
        <div className="mt-3 rounded-xl px-4 py-3 text-xs"
             style={{ background: '#fffbea', border: `1px solid ${C.gold}`, color: C.ink }}>
          <p className="font-medium">
            {data.not_in_pool_count} more {data.not_in_pool_count === 1 ? 'person is' : 'people are'}{' '}
            marked selected on an old JM requirement but {data.not_in_pool_count === 1 ? 'is' : 'are'} not
            in the candidate database, so no BGV case can be opened for them:
          </p>
          <p className="mt-1" style={{ color: C.muted }}>
            {data.not_in_pool.map((p) => p.name).join(', ')}
          </p>
        </div>
      )}
    </div>
  );
}

// ===========================================================================
// The candidate picker — a real, debounced, server-side search
// ===========================================================================

function CandidatePicker({ onPick, autoFocus = true }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [state, setState] = useState('idle');   // idle | searching | ready | error
  const [error, setError] = useState('');

  // Every request gets a sequence number and only the newest one is allowed to
  // write to state. Without this, a slow response for "ra" can land after the
  // fast one for "rahul" and replace the right answer with a stale one — which
  // reads to the user as "search doesn't work".
  const seq = useRef(0);

  const run = useCallback(async (term) => {
    const mine = ++seq.current;
    setState('searching');
    setError('');
    try {
      const { data } = await api.get('/bgv/candidate-search', {
        params: { q: term, limit: 25 },
      });
      if (mine !== seq.current) return;          // a newer search has overtaken us
      setResults(data.results || []);
      setState('ready');
    } catch (e) {
      if (mine !== seq.current) return;
      setResults([]);
      setError(`Search failed — ${errText(e)}`);
      setState('error');
    }
  }, []);

  // Debounced so a fast typist fires one request, not twelve. The timer is
  // cleared on unmount so a pending search cannot set state on a dead component.
  useEffect(() => {
    const t = setTimeout(() => { run(q.trim()); }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [q, run]);

  return (
    <div>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4"
                style={{ color: C.muted }} />
        <Input value={q} onChange={(e) => setQ(e.target.value)} autoFocus={autoFocus}
               className="pl-10"
               placeholder="Type a name, email address or mobile number…"
               data-testid="bgv-candidate-search" />
        {state === 'searching' && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin"
                   style={{ color: C.muted }} />
        )}
      </div>

      <div className="mt-2"><ErrorNote message={error} onRetry={() => run(q.trim())} /></div>

      <div className="mt-2 max-h-64 divide-y overflow-y-auto rounded-lg"
           style={{ borderColor: C.border, border: `1px solid ${C.border}` }}>
        {state === 'ready' && results.length === 0 ? (
          <p className="p-3 text-sm" style={{ color: C.muted }}>
            {q.trim()
              ? `Nobody in the database matches “${q.trim()}”. Try part of the name, the email address, or the last few digits of the mobile number.`
              : 'No candidates in the database yet.'}
          </p>
        ) : (
          results.map((person) => (
            <button key={person.id} type="button"
                    disabled={!!person.existing_case_id}
                    onClick={() => onPick(person)}
                    className="w-full px-3 py-2 text-left transition-colors hover:bg-slate-50 disabled:opacity-60"
                    data-testid="bgv-candidate-option">
              <CandidateLine person={person} right={
                person.existing_case_id
                  ? <Badge className="bg-slate-100 text-slate-600">Case open</Badge>
                  : person.pending_request_id
                    ? <Badge className="bg-amber-100 text-amber-800">Sent to BGV</Badge>
                    : null
              } />
            </button>
          ))
        )}
      </div>
      <p className="mt-1.5 text-[11px]" style={{ color: C.muted }}>
        Searches every candidate in the database — not just the ones on screen.
      </p>
    </div>
  );
}

// ===========================================================================
// New case dialog
// ===========================================================================

function NewCaseDialog({ open, onClose, onCreated }) {
  const [picked, setPicked] = useState(null);
  const [requirementId, setRequirementId] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setPicked(null); setRequirementId(null); } }, [open]);

  const submit = async () => {
    if (!picked) { toast.error('Choose a candidate first.'); return; }
    setBusy(true);
    try {
      const { data } = await api.post('/bgv/cases', {
        candidate_id: picked.id,
        requirement_id: requirementId || null,
      });
      toast.success('BGV case created. The candidate has been asked for their documents.');
      if (data.requirement_missing) {
        toast.message('No role is linked yet — you can link one on the case, and must before completing it.');
      }
      onCreated(data.case.id);
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle className="flex items-center gap-2">
          <ShieldCheck className="w-5 h-5" style={{ color: C.gold }} /> New BGV Case
        </DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-sm" style={{ color: C.muted }}>
            Opens a case in <b>Awaiting Docs</b> with the four checks, and emails the
            candidate a secure link to upload their documents.
          </p>

          {picked ? (
            <div className="rounded-lg px-3 py-2"
                 style={{ background: C.cream, border: `1px solid ${C.gold}` }}>
              <CandidateLine person={picked} right={
                <button onClick={() => { setPicked(null); setRequirementId(null); }}
                        className="flex-shrink-0 text-xs underline hover:no-underline"
                        style={{ color: C.muted }}>Change</button>
              } />
            </div>
          ) : (
            <div>
              <Label>Candidate</Label>
              <CandidatePicker onPick={setPicked} />
            </div>
          )}

          {picked && (
            <div>
              <Label>Role (optional)</Label>
              <p className="mb-1 text-xs" style={{ color: C.muted }}>
                Leave this blank and the ATS will attach the role itself if it can find one.
                A role is only required before the case is completed, because the signed
                certificate prints it.
              </p>
              <RequirementPicker candidateId={picked.id} value={requirementId}
                                 onChange={setRequirementId} />
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={submit} disabled={busy || !picked}>
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    : <Plus className="w-4 h-4 mr-2" />}
              Start BGV
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ===========================================================================
// Requirement picker — the thing the old error message never offered
// ===========================================================================

function RequirementPicker({ candidateId, value, onChange }) {
  const [q, setQ] = useState('');
  const [suggested, setSuggested] = useState([]);
  const [all, setAll] = useState([]);
  const [total, setTotal] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const seq = useRef(0);

  const load = useCallback(async (term) => {
    const mine = ++seq.current;
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get('/bgv/requirement-options', {
        params: { candidate_id: candidateId || '', q: term, limit: 25 },
      });
      if (mine !== seq.current) return;
      setSuggested(data.suggested || []);
      setAll(data.all || []);
      setTotal(data.total_requirements);
    } catch (e) {
      if (mine !== seq.current) return;
      setError(`Could not load roles — ${errText(e)}`);
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, [candidateId]);

  useEffect(() => {
    const t = setTimeout(() => load(q.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [q, load]);

  // Suggestions first, then everything else, with the suggestions de-duplicated
  // out of the long list so a role never appears twice.
  const suggestedIds = new Set(suggested.map((r) => r.id));
  const rest = all.filter((r) => !suggestedIds.has(r.id));

  // A render helper, deliberately not a component: a component declared inside
  // another component's body is a new type on every render, so React unmounts
  // and remounts the whole list on each keystroke.
  const renderRow = (role, keyPrefix, hint) => (
    <button key={`${keyPrefix}-${role.id}`} type="button" onClick={() => onChange(role.id)}
            className="flex w-full items-center gap-2 px-3 py-2 text-left transition-colors hover:bg-slate-50"
            style={value === role.id ? { background: C.cream } : undefined}
            data-testid="bgv-requirement-option">
      <Briefcase className="w-3.5 h-3.5 flex-shrink-0" style={{ color: C.muted }} />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm" style={{ color: C.ink }}>{role.title}</span>
        <span className="block truncate text-xs" style={{ color: C.muted }}>
          {[role.location, role.status, hint].filter(Boolean).join(' · ')}
        </span>
      </span>
      {value === role.id && <Check className="w-4 h-4" style={{ color: C.gold }} />}
    </button>
  );

  return (
    <div>
      <Input value={q} onChange={(e) => setQ(e.target.value)} className="h-9"
             placeholder="Search roles by title, client or location…" />
      <ErrorNote message={error} onRetry={() => load(q.trim())} />
      <div className="mt-2 max-h-56 divide-y overflow-y-auto rounded-lg"
           style={{ border: `1px solid ${C.border}` }}>
        {loading && (
          <div className="flex items-center gap-2 p-3 text-sm" style={{ color: C.muted }}>
            <Loader2 className="w-4 h-4 animate-spin" /> Loading roles…
          </div>
        )}
        {!loading && suggested.length > 0 && (
          <div className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide"
               style={{ background: C.cream, color: C.muted }}>
            Already worked on by this candidate
          </div>
        )}
        {!loading && suggested.map((r) => renderRow(r, 's', 'suggested'))}
        {!loading && rest.length > 0 && suggested.length > 0 && (
          <div className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide"
               style={{ background: C.cream, color: C.muted }}>
            All roles
          </div>
        )}
        {!loading && rest.map((r) => renderRow(r, 'a'))}
        {!loading && suggested.length === 0 && rest.length === 0 && (
          <p className="p-3 text-sm" style={{ color: C.muted }}>
            {total === 0
              ? 'There are no requirements in the ATS yet. Add one on the Requirements page (Client Work → Requirements), then come back here.'
              : 'No role matches that search.'}
          </p>
        )}
      </div>
      {value && (
        <button type="button" onClick={() => onChange(null)}
                className="mt-1 text-xs underline hover:no-underline" style={{ color: C.muted }}>
          Clear selection
        </button>
      )}
    </div>
  );
}

// ===========================================================================
// Case detail
// ===========================================================================

function CaseDetail({ caseId, isAdmin, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get(`/bgv/cases/${caseId}`);
      setData(res.data);
    } catch (e) {
      setError(`Could not load this case — ${errText(e)}`);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => { load(); }, [load]);

  const put = async (path, body, okMsg) => {
    setBusy(true);
    try {
      await api.put(path, body);
      toast.success(okMsg || 'Saved');
      await load();
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  const post = async (path, body, okMsg) => {
    setBusy(true);
    try {
      const res = await api.post(path, body);
      toast.success(okMsg || 'Done');
      await load();
      return res.data;
    } catch (e) {
      toast.error(errText(e));
      return null;
    } finally {
      setBusy(false);
    }
  };

  // Mint the 30-day signed link the client is sent, and put it on the
  // clipboard. This is JM's shareable report link — the ATS previously had no
  // way at all to give a client or a candidate the certificate.
  const shareReport = async () => {
    try {
      const res = await api.post(`/bgv/cases/${caseId}/share-report`);
      const url = res?.data?.url || '';
      try {
        await navigator.clipboard.writeText(url);
        toast.success('Share link copied — valid 30 days');
      } catch (clipboardError) {
        // Clipboard is blocked outside a secure context; show the link instead
        // of pretending it was copied.
        window.prompt('Copy this link (valid 30 days):', url);
      }
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const downloadCertificate = async () => {
    try {
      const res = await api.get(`/bgv/cases/${caseId}/certificate`, { responseType: 'blob' });
      window.open(URL.createObjectURL(res.data), '_blank');
    } catch (e) {
      // A blob-typed axios error hides its message inside the blob, so the
      // usual errText() would show "Request failed with status code 400" and
      // lose the sentence that says what to do.
      let detail = '';
      try {
        detail = JSON.parse(await e?.response?.data?.text?.())?.detail || '';
      } catch (parseError) {
        detail = '';
      }
      toast.error(detail || errText(e));
    }
  };

  if (loading) {
    return (
      <div className="p-6" style={{ background: C.cream, fontFamily: FONT }}>
        <Button variant="ghost" onClick={onBack} className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin" style={{ color: C.muted }} />
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-6" style={{ background: C.cream, fontFamily: FONT }}>
        <Button variant="ghost" onClick={onBack} className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>
        <ErrorNote message={error} onRetry={load} />
      </div>
    );
  }

  const {
    case: c, candidate, client, requirement, stages, documents, events, serial,
    derived_verdict: derivedVerdict, hours_left: hoursLeft,
    requirement_missing: requirementMissing,
    requirement_suggestions: requirementSuggestions,
    requirement_title: requirementTitle,
  } = data;
  const stageByKind = Object.fromEntries((stages || []).map((s) => [s.kind, s]));

  return (
    <div className="p-4 sm:p-6" style={{ background: C.cream, fontFamily: FONT, minHeight: '100%' }}
         data-testid="bgv-case-detail">
      <Button variant="ghost" onClick={onBack} className="mb-4">
        <ArrowLeft className="w-4 h-4 mr-2" /> Back to cases
      </Button>

      <ErrorNote message={error} onRetry={load} />

      {/* The banner that replaced the old dead-end error message. */}
      {requirementMissing && (
        <RequirementLinkBanner
          caseId={caseId}
          candidateId={c.candidate_id}
          suggestions={requirementSuggestions || []}
          canEdit={isAdmin}
          onLinked={load}
        />
      )}

      <Card className="mb-6"><CardContent className="p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-xl font-bold" style={{ color: C.ink }}>
                {candidate?.full_name || candidate?.name || 'Candidate'}
              </h2>
              <Badge className={caseStatusColor(c.status)}>
                {caseStatusIcon(c.status)}
                <span className="ml-1">{CASE_STATUS_LABEL[c.status]}</span>
              </Badge>
              <Badge variant="outline">Verdict: {derivedVerdict}</Badge>
            </div>
            <p className="mt-1 text-sm" style={{ color: C.muted }}>
              {requirementTitle || requirement?.position_title || 'No role linked'}
              {client?.name ? ` · ${client.name}` : ''}
            </p>
            <p className="mt-1 text-xs" style={{ color: C.muted }}>
              {serial} · Case {String(c.id).slice(0, 8).toUpperCase()}
              {c.status === 'awaiting_docs' && ` · ${hoursLeft}h left to upload`}
            </p>
          </div>
          <div className="flex flex-col items-end gap-2">
            <div className="flex items-center gap-2">
              <span className="text-xs" style={{ color: C.muted }}>Case status</span>
              <Select value={c.status} disabled={!isAdmin}
                      onValueChange={(s) => put(`/bgv/cases/${caseId}/status`, { status: s },
                                                `Case set to ${CASE_STATUS_LABEL[s]}`)}>
                <SelectTrigger className="w-40 h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CASE_STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>{CASE_STATUS_LABEL[s]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs" style={{ color: C.muted }}>Verdict</span>
              <Select value={c.verdict || 'none'} disabled={!isAdmin}
                      onValueChange={(v) => put(`/bgv/cases/${caseId}/verdict`,
                                                { verdict: v === 'none' ? null : v },
                                                'Verdict updated')}>
                <SelectTrigger className="w-40 h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not set</SelectItem>
                  {VERDICTS.map((v) => <SelectItem key={v} value={v}>{VERDICT_LABEL[v]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {c.status === 'completed' && (
              <div className="flex gap-2">
                {isAdmin && (
                  <Button size="sm" variant="outline" className="h-8 text-xs" disabled={busy}
                          onClick={() => post(`/bgv/cases/${caseId}/issue-certificate`, null,
                                              'Certificate regenerated')}>
                    <FileCheck2 className="w-3.5 h-3.5 mr-1" /> Re-issue
                  </Button>
                )}
                <Button size="sm" variant="outline" className="h-8 text-xs"
                        onClick={shareReport}>
                  <Link2 className="w-3.5 h-3.5 mr-1" /> Share link
                </Button>
                <Button size="sm" className="h-8 text-xs" onClick={downloadCertificate}>
                  <Download className="w-3.5 h-3.5 mr-1" /> Certificate
                </Button>
              </div>
            )}
          </div>
        </div>
      </CardContent></Card>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <SectionTitle icon={ShieldCheck}>Verification findings</SectionTitle>
          {STAGE_KINDS.map((kind) => {
            const s = stageByKind[kind] || { status: 'pending' };
            return (
              <Card key={kind}><CardContent className="p-4">
                <div className="mb-2 flex items-center justify-between gap-3">
                  <span className="font-semibold" style={{ color: C.ink }}>{STAGE_LABEL[kind]}</span>
                  <div className="flex items-center gap-2">
                    <Select value={s.status} disabled={!isAdmin}
                            onValueChange={(st) => put(`/bgv/cases/${caseId}/stages/${kind}`,
                                                       { status: st },
                                                       `${STAGE_LABEL[kind]} → ${STAGE_STATUS_LABEL[st]}`)}>
                      <SelectTrigger className={`w-40 h-8 text-xs border ${stageStatusColor(s.status)}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STAGE_STATUSES.map((st) => (
                          <SelectItem key={st} value={st}>{STAGE_STATUS_LABEL[st]}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {isAdmin && (
                      <Button size="sm" variant="ghost" className="h-8 text-xs"
                              title="Ask the candidate to upload this document again"
                              onClick={() => post(`/bgv/cases/${caseId}/request-reupload`,
                                                  { stage_kind: kind }, 'Re-upload requested')}>
                        <Send className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
                <StageNotes caseId={caseId} kind={kind} initial={s.verifier_notes || ''}
                            readOnly={!isAdmin} onSaved={load} />
                {s.finalized_at && (
                  <p className="mt-2 text-xs" style={{ color: C.muted }}>
                    Finalized {new Date(s.finalized_at).toLocaleString()}
                  </p>
                )}
              </CardContent></Card>
            );
          })}
        </div>

        <div className="space-y-4">
          <DocumentsPanel caseId={caseId} documents={documents || []} onChanged={load} />
          <Card><CardContent className="p-4">
            <SectionTitle icon={FileText}>Activity</SectionTitle>
            <div className="max-h-72 space-y-2 overflow-y-auto">
              {(events || []).length === 0 && (
                <p className="text-xs" style={{ color: C.muted }}>No activity yet.</p>
              )}
              {(events || []).map((e) => (
                <div key={e.id} className="border-l-2 pl-2 text-xs"
                     style={{ borderColor: C.border }}>
                  <span className="font-medium" style={{ color: C.ink }}>{e.kind}</span>
                  {e.channel !== 'internal' && (
                    <span style={{ color: C.muted }}> · {e.channel}/{e.status}</span>
                  )}
                  {e.template_requested && e.status === 'skipped' && (
                    <span className="text-amber-600">
                      {' '}(WhatsApp {e.template_requested} not approved yet — emailed instead)
                    </span>
                  )}
                  <div style={{ color: C.muted }}>
                    {e.detail || ''}
                    {e.created_at && ` · ${new Date(e.created_at).toLocaleString()}`}
                  </div>
                </div>
              ))}
            </div>
          </CardContent></Card>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The "no role linked" banner
// ---------------------------------------------------------------------------

function RequirementLinkBanner({ caseId, candidateId, suggestions, canEdit, onLinked }) {
  const [open, setOpen] = useState(false);
  const [choice, setChoice] = useState(null);
  const [busy, setBusy] = useState(false);

  const link = async (requirementId) => {
    setBusy(true);
    try {
      const { data } = await api.post('/bgv/link-requirement', {
        candidate_id: candidateId,
        requirement_id: requirementId,
        case_id: caseId,
      });
      toast.success(data.message || 'Role linked.');
      setOpen(false);
      onLinked && onLinked();
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mb-4 rounded-xl px-4 py-3"
         style={{ background: '#fffbea', border: `1px solid ${C.gold}` }}
         data-testid="bgv-requirement-banner">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="flex items-center gap-2 text-sm font-semibold" style={{ color: C.ink }}>
            <Link2 className="w-4 h-4" /> No role linked to this case
          </p>
          <p className="mt-1 text-xs" style={{ color: C.muted }}>
            Verification can go ahead without one. You only need a role before you mark the
            case <b>Completed</b>, because the signed certificate prints the job title and
            the client&apos;s name.
          </p>
        </div>
        {canEdit && (
          <Button size="sm" variant="outline" onClick={() => setOpen((v) => !v)}>
            <Link2 className="w-3.5 h-3.5 mr-1" /> Link a role
          </Button>
        )}
      </div>

      {canEdit && open && (
        <div className="mt-3">
          {suggestions.length > 0 && (
            <div className="mb-3">
              <p className="mb-1 text-xs font-medium" style={{ color: C.ink }}>
                This candidate is already on {suggestions.length === 1 ? 'this role' : 'these roles'} —
                one click is usually all it takes:
              </p>
              <div className="flex flex-wrap gap-2">
                {suggestions.map((r) => (
                  <Button key={r.id} size="sm" variant="outline" disabled={busy}
                          onClick={() => link(r.id)}>
                    <Briefcase className="w-3.5 h-3.5 mr-1" /> {r.title}
                    {r.location ? ` · ${r.location}` : ''}
                  </Button>
                ))}
              </div>
            </div>
          )}
          <p className="mb-1 text-xs font-medium" style={{ color: C.ink }}>
            Or pick any other role:
          </p>
          <RequirementPicker candidateId={candidateId} value={choice} onChange={setChoice} />
          <div className="mt-2 flex justify-end">
            <Button size="sm" disabled={!choice || busy} onClick={() => link(choice)}>
              {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                    : <Check className="w-3.5 h-3.5 mr-1" />}
              Link this role
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stage notes and documents (behaviour unchanged; moved onto `api`)
// ---------------------------------------------------------------------------

function StageNotes({ caseId, kind, initial, readOnly, onSaved }) {
  const [notes, setNotes] = useState(initial);
  const [dirty, setDirty] = useState(false);
  useEffect(() => { setNotes(initial); setDirty(false); }, [initial]);

  const save = async () => {
    try {
      // status omitted -> notes-only update (no stage move, no notification)
      await api.put(`/bgv/cases/${caseId}/stages/${kind}`, { verifier_notes: notes });
      toast.success('Notes saved');
      setDirty(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  return (
    <div>
      <textarea
        className="min-h-[60px] w-full rounded-md border p-2 text-sm focus:outline-none focus:ring-1"
        style={{ borderColor: C.border }}
        placeholder="Verifier notes (printed on the certificate)"
        value={notes}
        readOnly={readOnly}
        onChange={(e) => { setNotes(e.target.value); setDirty(true); }}
      />
      {dirty && !readOnly && (
        <Button size="sm" variant="outline" className="mt-1 h-7 text-xs" onClick={save}>
          Save notes
        </Button>
      )}
    </div>
  );
}

function DocumentsPanel({ caseId, documents, onChanged }) {
  const [showAdd, setShowAdd] = useState(false);
  const [kind, setKind] = useState('salary_ctc');
  const [fileName, setFileName] = useState('');
  const [busy, setBusy] = useState(false);

  const add = async () => {
    if (!fileName.trim()) { toast.error('Give the document a file name.'); return; }
    setBusy(true);
    try {
      await api.post(`/bgv/cases/${caseId}/documents`, {
        stage_kind: kind, file_name: fileName.trim(), uploaded_source: 'admin',
      });
      toast.success('Document recorded');
      setFileName('');
      setShowAdd(false);
      onChanged && onChanged();
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card><CardContent className="p-4">
      <SectionTitle icon={FileText} action={
        <Button size="sm" variant="outline" className="h-7 text-xs"
                onClick={() => setShowAdd((v) => !v)}>
          <Upload className="w-3.5 h-3.5 mr-1" /> Add
        </Button>
      }>Documents</SectionTitle>

      {showAdd && (
        <div className="mb-3 space-y-2 rounded-md p-2" style={{ background: C.cream }}>
          <Select value={kind} onValueChange={setKind}>
            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              {STAGE_KINDS.map((k) => <SelectItem key={k} value={k}>{STAGE_LABEL[k]}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input className="h-8 text-xs" placeholder="File name (e.g. Rahul-payslip.pdf)"
                 value={fileName} onChange={(e) => setFileName(e.target.value)} />
          <Button size="sm" className="h-7 w-full text-xs" onClick={add} disabled={busy}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Record document'}
          </Button>
        </div>
      )}

      <div className="space-y-2">
        {documents.length === 0 && (
          <p className="text-xs" style={{ color: C.muted }}>No documents on file.</p>
        )}
        {documents.map((d) => (
          <div key={d.id} className="flex items-center gap-2 text-xs">
            <FileText className="w-3.5 h-3.5 flex-shrink-0" style={{ color: C.muted }} />
            <span className="flex-1 truncate" style={{ color: C.ink }}>{d.file_name}</span>
            <Badge variant="outline" className="text-[10px]">
              {STAGE_LABEL[d.stage_kind] || d.stage_kind}
            </Badge>
            {d.uploaded_source === 'admin' && <span style={{ color: C.muted }}>admin</span>}
          </div>
        ))}
      </div>
    </CardContent></Card>
  );
}
